/**
 * Contact_Intake screen (application-form-builder spec, task 21.1).
 *
 * The fixed, **non-configurable** baseline `Contact_Intake_Form` a new account
 * completes before reaching any application (Req 14.1). It collects Name (first
 * + last), Birthday, Email, Phone, and Address — **all mandatory** (Req 14.2) —
 * and on submit calls `applicationFormClient.submitContactIntake`, which
 * creates or completes the global Person SSOT in one idempotent operation
 * (Req 14.4). A Director cannot reconfigure this form's shape; it is hand-built
 * here rather than rendered from a FormVersion.
 *
 * ## Validation parity (Req 14.3)
 *
 * The canonical mandatory-field rule lives in the shared pure module
 * `infrastructure/lambda/application-form/contactIntake.ts#validateContactIntake`.
 * The Form_Renderer reuses backend pure modules directly (see
 * `frontend/src/lib/application-form/deriveZodSchema.ts`), but `contactIntake.ts`
 * transitively imports `person-service/personLogic.ts`, which imports Node's
 * `crypto` (`createHash`) for the deterministic `personId` derivation. Pulling
 * that into the browser bundle would break the Vite build — which is exactly
 * why the mock client (task 19.2) deliberately avoided importing it. So instead
 * of importing `validateContactIntake`, this screen mirrors *the same
 * field-presence checks it performs* with a Zod schema: every scalar
 * (legalFirstName, legalLastName, dateOfBirth, primaryEmail, mobilePhone) and
 * every Address component (street, city, state, zip) must be non-blank after
 * trimming — identical to `validateContactIntake` / `isAddressBlank`. The
 * resolver still runs the real `validateContactIntake` server-side (task 17.1),
 * so the screen's check is a faithful client mirror, never the source of truth.
 *
 * ## Accessibility (Req 14.1, WCAG AA)
 *
 * Each control uses the app's shadcn/ui `Label` + `Input` primitives with an
 * explicit `htmlFor`/`id` pairing, `aria-required`, `aria-invalid` driven by
 * validation state, and `aria-describedby` wiring the control to its help-text
 * and/or error slots. Errors render in a `role="alert"` slot and the submit
 * surfaces a `role="alert"` summary; the form sets focus to the first invalid
 * control so keyboard and screen-reader users are taken straight to what needs
 * fixing.
 *
 * Validates: Requirements 14.1, 14.2, 14.3, 14.4
 */

import React, { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { CoBrand } from '@/components/ui/co-brand';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { applicationFormClient } from '@/lib/application-form/client';
import type { ContactIntakeInput } from '@/lib/application-form/client';
import { US_STATES } from '@/lib/usStates';

// ---------------------------------------------------------------------------
// Client-side schema — mirrors `contactIntake.ts#validateContactIntake`
// ---------------------------------------------------------------------------

/** A non-blank-after-trim string, mirroring `contactIntake.ts#isBlank`. */
const requiredField = (label: string) =>
  z
    .string({ required_error: `${label} is required.`, invalid_type_error: `${label} is required.` })
    .trim()
    .min(1, `${label} is required.`);

// ---------------------------------------------------------------------------
// Format/date validation (Req 2.3)
//
// These layer *on top of* the presence schema above. The authoritative check
// is still the server-side `validateContactIntake` (task 17.1); this is a
// client-side convenience that surfaces a per-field message and withholds the
// `submitContactIntake` call for an obviously-malformed non-blank value. Each
// refinement is written to *pass* on a blank value so the presence check owns
// the blank case — only non-blank fields are format/date checked, and a blank
// field still reports the single "… is required." message.
// ---------------------------------------------------------------------------

/**
 * RFC-pragmatic email check — a single, deliberately permissive regex (not a
 * full RFC 5322 grammar): one or more non-space/non-`@` characters, an `@`, a
 * non-space/non-`@` domain, a dot, and a non-space/non-`@` TLD. This rejects the
 * common mistakes (missing `@`, missing domain/TLD, embedded spaces) without
 * pretending to be authoritative.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Characters a phone entry may legitimately contain: decimal digits plus the
 * common presentation glyphs (spaces, dashes, parentheses, dots) and an
 * optional leading `+` country-code marker. Anything else — letters, `#`, `*`,
 * etc. — makes the value not a phone number, so the refinement rejects it
 * before the digit-shape check ever runs.
 */
const PHONE_ALLOWED_CHARS = /^[+()\-.\s\d]+$/;

// ---------------------------------------------------------------------------
// Submit timeout (Req 2.7)
//
// `submitContactIntake` is raced against this limit so a request that never
// settles (a hung network call) is treated as a failure rather than leaving the
// Contestant on a permanently-disabled submit control. On timeout the rejection
// flows into the same catch path as a returned error: the error message is
// shown, the submit control is re-enabled, and entered values are retained.
// ---------------------------------------------------------------------------

/** Maximum time (ms) to wait for `submitContactIntake` before treating it as a failure. */
const SUBMIT_TIMEOUT_MS = 30_000;

/** Message shown when the submit request exceeds {@link SUBMIT_TIMEOUT_MS}. */
const SUBMIT_TIMEOUT_MESSAGE =
  'Submitting your contact details timed out. Please check your connection and try again.';

/**
 * True when `value` is a valid North-American (NANP) phone number.
 *
 * A stricter check than a bare digit count: the entry may use only the allowed
 * presentation glyphs ({@link PHONE_ALLOWED_CHARS}), and once formatting is
 * stripped it must be a 10-digit number — or 11 digits led by a `1`
 * country-code — whose area code and central-office (exchange) code each begin
 * with a digit 2–9, exactly as the North-American Numbering Plan requires.
 * This rejects the common malformations (too few/too many digits, a leading `0`
 * or `1` area/exchange code, letters or stray symbols) rather than accepting
 * anything with ten digits in it.
 */
function isValidPhone(value: string): boolean {
  if (!PHONE_ALLOWED_CHARS.test(value)) return false;

  let digits = value.replace(/\D/g, '');
  // Drop a leading `1` country code (`+1 …` / `1-…`) before shape-checking.
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }

  // NANP: [area][exchange][subscriber] = NXX-NXX-XXXX, N ∈ 2–9.
  return /^[2-9]\d{2}[2-9]\d{6}$/.test(digits);
}

/**
 * True when `value` is a real calendar date in `YYYY-MM-DD` form (the shape the
 * native date control emits) that falls on or before today. Rejects malformed
 * shapes, non-existent calendar dates (e.g. `2023-02-30`, which `Date` would
 * silently roll over to March), and any future date.
 */
function isValidBirthday(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const date = new Date(year, month - 1, day);
  // Reject roll-over dates: the constructed date must echo the input parts back.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date.getTime() <= today.getTime();
}

/**
 * The contact-intake schema. Field labels match the messages
 * `validateContactIntake` emits ("First name", "Last name", "Birthday",
 * "Email", "Phone number"); the structured Address is validated component by
 * component (street/city/state/zip), faithfully reproducing `isAddressBlank`'s
 * "every component non-blank" rule while giving each control its own
 * accessible error slot.
 *
 * Email, Phone, and Birthday carry an additional format/date refinement
 * (Req 2.3) layered onto their presence check; each refinement passes a blank
 * value through so the presence check remains the source of the "required"
 * message for empty fields.
 */
const contactIntakeSchema = z.object({
  legalFirstName: requiredField('First name'),
  legalLastName: requiredField('Last name'),
  dateOfBirth: requiredField('Birthday').refine(
    (value) => value === '' || isValidBirthday(value),
    { message: 'Enter a valid birthday on or before today.' },
  ),
  primaryEmail: requiredField('Email').refine(
    (value) => value === '' || EMAIL_PATTERN.test(value),
    { message: 'Enter a valid email address.' },
  ),
  mobilePhone: requiredField('Phone number').refine(
    (value) => value === '' || isValidPhone(value),
    { message: 'Enter a valid 10-digit US phone number.' },
  ),
  address: z.object({
    street: requiredField('Street address'),
    city: requiredField('City'),
    state: requiredField('State'),
    zip: requiredField('ZIP code'),
  }),
});

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

/** The flat, fully-controlled form state. Address is nested to match the DTO. */
interface ContactIntakeFormState {
  legalFirstName: string;
  legalLastName: string;
  dateOfBirth: string;
  primaryEmail: string;
  mobilePhone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
}

/** Dotted-path keys for every editable control, used for errors + focus. */
type FieldPath =
  | 'legalFirstName'
  | 'legalLastName'
  | 'dateOfBirth'
  | 'primaryEmail'
  | 'mobilePhone'
  | 'address.street'
  | 'address.city'
  | 'address.state'
  | 'address.zip';

const EMPTY_STATE: ContactIntakeFormState = {
  legalFirstName: '',
  legalLastName: '',
  dateOfBirth: '',
  primaryEmail: '',
  mobilePhone: '',
  address: { street: '', city: '', state: '', zip: '' },
};

export interface ContactIntakeFormProps {
  /**
   * Called after a successful submit with the resolved Person id. The
   * onboarding router (task 21.2) uses this to advance to the next destination.
   */
  onComplete?: (personId: string) => void;
  /** Optional starting values (e.g. prefilled email from the account). */
  initialValues?: Partial<ContactIntakeFormState>;
}

/** Collapse a Zod issue path (e.g. `['address','zip']`) into a `FieldPath`. */
function pathToKey(path: (string | number)[]): FieldPath {
  return path.join('.') as FieldPath;
}

/**
 * Read the current value the browser is *displaying* for `name` from the live
 * form DOM, falling back to `fallback` (the React state value) when the control
 * can't be found.
 *
 * ## Why read the DOM at submit (the birthday-picker bug)
 *
 * The form is fully controlled, so validation normally reads React state. But
 * React only mirrors a control into state when its synthetic `onChange` fires,
 * and that does not reliably happen when the value is set by the browser
 * rather than by a keystroke — the native date picker / autofill helper (the
 * Birthday field carries `autoComplete="bday"`) writes straight to the DOM and
 * updates React's internal value tracker in lock-step, so the follow-up
 * `onChange` React would use to sync state never runs. The field then *shows* a
 * date while `values.dateOfBirth` is still `''`, and the "Birthday is required."
 * presence check (which reads state) fails — even though the user picked a date.
 * Typing goes through `onChange`, so state fills in and the bug doesn't appear.
 *
 * Reading each control's live DOM value on submit closes that gap: whatever the
 * browser is displaying (picked, autofilled, or typed) is what we validate and
 * submit, exactly as a native form submission would.
 */
function readControlValue(
  form: HTMLFormElement,
  name: FieldPath,
  fallback: string,
): string {
  const el = form.elements.namedItem(name);
  if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) {
    return el.value;
  }
  return fallback;
}

/**
 * Reconstruct the full form state from the live DOM (with React state as the
 * per-field fallback), so a value the browser wrote without firing `onChange`
 * — a picked/autofilled birthday, an autofilled address — is still captured.
 */
function readFormState(
  form: HTMLFormElement,
  state: ContactIntakeFormState,
): ContactIntakeFormState {
  return {
    legalFirstName: readControlValue(form, 'legalFirstName', state.legalFirstName),
    legalLastName: readControlValue(form, 'legalLastName', state.legalLastName),
    dateOfBirth: readControlValue(form, 'dateOfBirth', state.dateOfBirth),
    primaryEmail: readControlValue(form, 'primaryEmail', state.primaryEmail),
    mobilePhone: readControlValue(form, 'mobilePhone', state.mobilePhone),
    address: {
      street: readControlValue(form, 'address.street', state.address.street),
      city: readControlValue(form, 'address.city', state.address.city),
      state: readControlValue(form, 'address.state', state.address.state),
      zip: readControlValue(form, 'address.zip', state.address.zip),
    },
  };
}

/**
 * The fixed, mandatory Contact_Intake screen. Renders the five mandatory groups
 * (Name, Birthday, Email, Phone, Address), validates them client-side against
 * the `validateContactIntake` mirror, and on success creates/completes the
 * global Person via `submitContactIntake` (Req 14.4).
 */
export function ContactIntakeForm({
  onComplete,
  initialValues,
}: ContactIntakeFormProps): React.ReactElement {
  const [values, setValues] = useState<ContactIntakeFormState>(() => ({
    ...EMPTY_STATE,
    ...initialValues,
    address: { ...EMPTY_STATE.address, ...initialValues?.address },
  }));
  const [errors, setErrors] = useState<Partial<Record<FieldPath, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const setScalar = useCallback(
    (key: Exclude<FieldPath, `address.${string}`>) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.value;
        setValues((prev) => ({ ...prev, [key]: next }));
        setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
      },
    [],
  );

  const setAddress = useCallback(
    (component: 'street' | 'city' | 'state' | 'zip') =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const next = e.target.value;
        const key = `address.${component}` as FieldPath;
        setValues((prev) => ({ ...prev, address: { ...prev.address, [component]: next } }));
        setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
      },
    [],
  );

  // Ordered list of field keys so "first invalid" matches visual order.
  const fieldOrder = useMemo<FieldPath[]>(
    () => [
      'legalFirstName',
      'legalLastName',
      'primaryEmail',
      'dateOfBirth',
      'mobilePhone',
      'address.street',
      'address.city',
      'address.state',
      'address.zip',
    ],
    [],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (submitting) return;
      setSubmitError(null);

      // Validate/submit against what the browser is actually displaying, not
      // just React state: a birthday set via the native date picker/autofill
      // helper lands in the DOM without always firing `onChange`, so state can
      // lag behind the visible value (see `readFormState`). Reading the live
      // DOM here captures picked/autofilled values and keeps state coherent.
      const domValues = readFormState(e.currentTarget, values);
      if (
        domValues.dateOfBirth !== values.dateOfBirth ||
        domValues.legalFirstName !== values.legalFirstName ||
        domValues.legalLastName !== values.legalLastName ||
        domValues.mobilePhone !== values.mobilePhone ||
        domValues.address.street !== values.address.street ||
        domValues.address.city !== values.address.city ||
        domValues.address.state !== values.address.state ||
        domValues.address.zip !== values.address.zip
      ) {
        setValues(domValues);
      }

      const parsed = contactIntakeSchema.safeParse(domValues);
      if (!parsed.success) {
        const nextErrors: Partial<Record<FieldPath, string>> = {};
        for (const issue of parsed.error.issues) {
          const key = pathToKey(issue.path);
          if (!nextErrors[key]) {
            nextErrors[key] = issue.message;
          }
        }
        setErrors(nextErrors);
        const firstInvalid = fieldOrder.find((key) => nextErrors[key]);
        if (firstInvalid) {
          // Controls carry their FieldPath as `id`, so getElementById moves
          // focus to the first invalid field without relying on the shadcn
          // Input forwarding a ref.
          document.getElementById(firstInvalid)?.focus();
        }
        return;
      }

      setErrors({});
      setSubmitting(true);
      let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
      try {
        const input: ContactIntakeInput = parsed.data;
        // Race the submit against a 30s limit (Req 2.7): a request that never
        // settles rejects with the timeout error and flows into the catch below.
        const timeout = new Promise<never>((_resolve, reject) => {
          timeoutHandle = setTimeout(() => {
            reject(new Error(SUBMIT_TIMEOUT_MESSAGE));
          }, SUBMIT_TIMEOUT_MS);
        });
        const { personId } = await Promise.race([
          applicationFormClient.submitContactIntake(input),
          timeout,
        ]);
        onComplete?.(personId);
      } catch (err) {
        setSubmitError(
          err instanceof Error
            ? err.message
            : 'Something went wrong submitting your contact details. Please try again.',
        );
      } finally {
        // Clear the pending timer so a settled request doesn't leak a handle.
        if (timeoutHandle !== undefined) {
          clearTimeout(timeoutHandle);
        }
        setSubmitting(false);
      }
    },
    [values, submitting, fieldOrder, onComplete],
  );

  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-2xl rounded-card border border-border bg-card p-8 shadow-subtle sm:p-10">
        <form
          onSubmit={handleSubmit}
          noValidate
          className="space-y-8"
          aria-labelledby="contact-intake-heading"
          data-testid="contact-intake-form"
        >
          <div className="border-b border-border pb-6 text-center">
            <span className="font-display text-2xl uppercase leading-none tracking-widest text-foreground">
              Idaho America Pageants
            </span>
          </div>
      <header className="space-y-2">
        <h1 id="contact-intake-heading" className="font-headline text-lg font-bold text-primary">
          Your contact details
        </h1>
        <p className="text-muted-foreground text-sm">
          We keep these details on file for your application. Please make sure they're complete and
          up to date — all fields are required.
        </p>
      </header>

      {/* Name */}
      <fieldset className="space-y-5 border-none p-0">
        <legend className="font-label text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Name
        </legend>
        <div className="grid gap-5 sm:grid-cols-2">
          <TextField
            id="legalFirstName"
            label="First name"
            value={values.legalFirstName}
            onChange={setScalar('legalFirstName')}
            error={errors.legalFirstName}
            autoComplete="given-name"
          />
          <TextField
            id="legalLastName"
            label="Last name"
            value={values.legalLastName}
            onChange={setScalar('legalLastName')}
            error={errors.legalLastName}
            autoComplete="family-name"
          />
        </div>
      </fieldset>

      {/* Email — verified at sign-up, locked here */}
      <TextField
        id="primaryEmail"
        label="Email"
        type="email"
        value={values.primaryEmail}
        onChange={setScalar('primaryEmail')}
        error={errors.primaryEmail}
        autoComplete="email"
        inputMode="email"
        readOnly
        helpText="Verified when you created your account."
      />

      {/* Birthday */}
      <TextField
        id="dateOfBirth"
        label="Birthday"
        type="date"
        value={values.dateOfBirth}
        onChange={setScalar('dateOfBirth')}
        error={errors.dateOfBirth}
        autoComplete="bday"
        helpText="Used to confirm eligibility for the divisions you can apply to."
      />

      {/* Phone */}
      <TextField
        id="mobilePhone"
        label="Phone number"
        type="tel"
        value={values.mobilePhone}
        onChange={setScalar('mobilePhone')}
        error={errors.mobilePhone}
        autoComplete="tel"
        inputMode="tel"
        placeholder="(208) 555-0100"
      />

      {/* Address */}
      <fieldset className="space-y-5 border-none p-0">
        <legend className="font-label text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Address
        </legend>
        <TextField
          id="address.street"
          label="Street address"
          value={values.address.street}
          onChange={setAddress('street')}
          error={errors['address.street']}
          autoComplete="street-address"
        />
        <div className="grid gap-5 sm:grid-cols-3">
          <TextField
            id="address.city"
            label="City"
            value={values.address.city}
            onChange={setAddress('city')}
            error={errors['address.city']}
            autoComplete="address-level2"
          />
          <SelectField
            id="address.state"
            label="State"
            value={values.address.state}
            onChange={setAddress('state')}
            error={errors['address.state']}
            autoComplete="address-level1"
            placeholder="Select a state"
            options={US_STATES.map((s) => ({ value: s.code, label: s.name }))}
          />
          <TextField
            id="address.zip"
            label="ZIP code"
            value={values.address.zip}
            onChange={setAddress('zip')}
            error={errors['address.zip']}
            autoComplete="postal-code"
            inputMode="numeric"
          />
        </div>
      </fieldset>

      {/* Submission-level error */}
      {submitError && (
        <div
          className="bg-destructive-muted text-destructive-text rounded-lg px-3 py-2 text-sm font-body"
          role="alert"
        >
          {submitError}
        </div>
      )}

      <div className="pt-2">
        <Button type="submit" size="lg" disabled={submitting} data-testid="contact-intake-submit">
          {submitting ? 'Saving…' : 'Continue'}
        </Button>
      </div>
        </form>
      </div>
      <CoBrand variant="watermark" className="mt-8" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// TextField — accessible label + control + help/error slots
// ---------------------------------------------------------------------------

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  helpText?: string;
  type?: React.HTMLInputTypeAttribute;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  placeholder?: string;
  readOnly?: boolean;
}

/**
 * A single labelled control with its help-text and error slots wired for
 * assistive tech: the `Label` is bound via `htmlFor`/`id`, the `Input` is marked
 * `aria-required` and (when invalid) `aria-invalid`, and `aria-describedby`
 * references whichever of the help/error slots are present. The error slot is a
 * `role="alert"` region so it is announced when it appears.
 */
function TextField({
  id,
  label,
  value,
  onChange,
  error,
  helpText,
  type = 'text',
  autoComplete,
  inputMode,
  placeholder,
  readOnly,
}: TextFieldProps): React.ReactElement {
  const helpId = helpText ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        <span className="text-destructive-text" aria-hidden="true">
          *
        </span>
      </Label>

      {helpText && (
        <p id={helpId} className="text-muted-foreground text-xs font-body">
          {helpText}
        </p>
      )}

      <Input
        id={id}
        name={id}
        type={type}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        inputMode={inputMode}
        placeholder={placeholder}
        readOnly={readOnly}
        aria-required
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          error && 'border-destructive',
          readOnly && 'cursor-not-allowed bg-muted text-muted-foreground',
        )}
      />

      {error && (
        <p id={errorId} className="text-destructive-text text-sm font-body" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SelectField — accessible label + native <select> + help/error slots
// ---------------------------------------------------------------------------

interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: readonly SelectOption[];
  error?: string;
  helpText?: string;
  autoComplete?: string;
  /** Label for the empty/placeholder option (value `''`). */
  placeholder?: string;
}

/**
 * A labelled native `<select>` that constrains the value to a fixed option set
 * (e.g. valid USPS state codes), wired for assistive tech exactly like
 * {@link TextField}: `Label` bound via `htmlFor`/`id`, `aria-required`,
 * `aria-invalid` on error, and `aria-describedby` referencing the help/error
 * slots. A disabled empty option renders the placeholder; selecting it leaves
 * the value blank so the existing presence validation reports "… is required.".
 */
function SelectField({
  id,
  label,
  value,
  onChange,
  options,
  error,
  helpText,
  autoComplete,
  placeholder = 'Select…',
}: SelectFieldProps): React.ReactElement {
  const helpId = helpText ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        <span className="text-destructive-text" aria-hidden="true">
          *
        </span>
      </Label>

      {helpText && (
        <p id={helpId} className="text-muted-foreground text-xs font-body">
          {helpText}
        </p>
      )}

      <select
        id={id}
        name={id}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        aria-required
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={cn(
          'border-input bg-background ring-offset-background focus-visible:ring-ring flex h-10 w-full rounded-input border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
          error && 'border-destructive',
        )}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {error && (
        <p id={errorId} className="text-destructive-text text-sm font-body" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default ContactIntakeForm;